'use client';
import { useAppSelector } from '@/redux/redux-hook';
import { Fragment, useEffect, useMemo, useState } from 'react';
import CarDisplayCardClient from '@/components/shared/car-details-card/car-details-car-client.component';
import ReserveSeeCarBanner from '@/components/shared/banners/reserve-vs-see-banner/reserve-vs-see-banner.component';
import ValuePropositionSlider from '@/components/shared/value-proposition-slider/value-proposition-slider.component';
import { useTranslation, useWindowDimensions } from '@/hook';
import { BannerData, CarListingData } from '@/types';
import styles from './styles.module.scss';
import { MakeModel } from '@/types/filter.types';
import { AVAILABLE_LANGUAGES } from '@/constants';
import FilterableContent, { buildUrl, TFilterTerm } from '@/utils/filterUtil';
import dynamic from 'next/dynamic';
import ConditionalClientComponent from '@/components/shared/conditional/conditional-client.component';
import { sortCars } from '@/utils';
import { useRouter } from 'next/navigation';
import colors from '@/theme/colors.module.scss';
import Typography from '@/components/shared/typography/typography.component';
import { DimensionsTargets } from '@/hook/use-window-width.hook';

const Sort = dynamic(
    () =>
        import('@/components/shared/sort/sort-desktop.component').then(
            mod => mod.default
        ),
    { ssr: false }
);
const RetailBanners = dynamic(
    () =>
        import('./retail-banners/retail-banners.component').then(
            mod => mod.default
        ),
    { ssr: false }
);
const Filters = dynamic(
    () =>
        import('@/components/filters/filter-desktop.component').then(
            mod => mod.default
        ),
    { ssr: false }
);
const FilterHeaderMobile = dynamic(
    () =>
        import(
            '@/components/filters/filter-header-mobile/filters-header-mobile.component'
        ).then(mod => mod.default),
    { ssr: false }
);

const CarsListing = ({
    exchangeCarBanner,
    carsData,
    initFilters,
    financeCarCard,
    cantFindCarCard,
    bannersData,
    comboList,
    bodyTypes,
    lang,
}: {
    exchangeCarBanner: JSX.Element;
    financeCarCard: JSX.Element;
    cantFindCarCard: JSX.Element;
    carsData: CarListingData[];
    initFilters: TFilterTerm<CarListingData>[];
    bannersData: BannerData[];
    comboList: MakeModel;
    bodyTypes: string[];
    lang: AVAILABLE_LANGUAGES;
}) => {
    const { t } = useTranslation();
    const { width = 300, IS_DESKTOP: desktopView = false } =
        useWindowDimensions(DimensionsTargets.MOBILE);
    const [listings] = useState<CarListingData[]>(carsData);
    const [countToRender, setCountToRender] = useState(14);
    const [exchangeCarBannerIndex, setExchangeCarBannerIndex] = useState(0);
    const router = useRouter();

    useEffect(() => {
        let targetIndex = 0;
        if (width > 1100) {
            targetIndex = 10;
        } else if (width >= 786) {
            targetIndex = 8;
        }

        setExchangeCarBannerIndex(targetIndex);
    }, [width, countToRender]);
    const sortingOptions = useAppSelector(state => state.sort);
    const [resultsCount, setResultsCount] = useState(carsData.length);
    const [filters, setFilters] =
        useState<TFilterTerm<CarListingData>[]>(initFilters);
    useEffect(() => {
        return () => {
            router.refresh();
        };
    }, []);

    const [filteredListings, setFilteredListings] = useState<CarListingData[]>(
        new FilterableContent<CarListingData>()
            .SetData(carsData)
            .ApplyFilters()
            .filteredData.slice(0, countToRender)
    );

    useEffect(() => {
        const renderAllCars = () => {
            setCountToRender(carsData.length);
        };
        window.addEventListener('scroll', renderAllCars);

        return () => {
            window.removeEventListener('scroll', renderAllCars);
        };
    }, []);

    useMemo(() => {
        const filterableVehicles = new FilterableContent<CarListingData>()
            .SetData(carsData)
            .SetFilters(filters)
            .ApplyFilters().filteredData;

        setResultsCount(filterableVehicles.length);
        setFilteredListings(filterableVehicles.slice(0, countToRender));
    }, [listings, filters, countToRender, exchangeCarBannerIndex]);

    useEffect(() => {
        window.history.replaceState(
            {},
            '',
            new URL(`${lang}${buildUrl(filters)}`, window.location.origin)
        );
    }, [listings, filters]);

    const [allowScroll, setAllowScroll] = useState(true);

    // _______________________________________________________

    const emptySessionStorageOnRefresh = () => {
        sessionStorage.setItem(
            'f6e73b048ec0b88fbb5e5a5da9d5f2a4a4e4f27ce4d8a1a96877f18983f02e2',
            'false'
        );
        sessionStorage.setItem('carId', '');
    };

    useEffect(() => {
        window.addEventListener('beforeunload', emptySessionStorageOnRefresh);

        return () => {
            window.removeEventListener(
                'beforeunload',
                emptySessionStorageOnRefresh
            );
        };
    }, []);
    useEffect(() => {
        const carId = sessionStorage.getItem('carId');
        const allowScrollBehaviour = sessionStorage.getItem(
            'f6e73b048ec0b88fbb5e5a5da9d5f2a4a4e4f27ce4d8a1a96877f18983f02e2'
        );
        if (carId && carId !== '' && allowScrollBehaviour === 'true') {
            scrollToCar();
        }
        setAllowScroll(false);
    }, [filteredListings, listings]);
    const isInView = (element: HTMLElement) => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        const windowHeight =
            window.innerHeight || document.documentElement.clientHeight;

        const vertInView =
            rect.top <= windowHeight && rect.top + rect.height >= 0;
        return vertInView;
    };
    const scrollToCar = () => {
        const carId = sessionStorage.getItem('carId');
        const allowScrollBehaviour = sessionStorage.getItem(
            'f6e73b048ec0b88fbb5e5a5da9d5f2a4a4e4f27ce4d8a1a96877f18983f02e2'
        );

        if (
            carId &&
            carId !== '' &&
            allowScrollBehaviour === 'true' &&
            allowScroll
        ) {
            const element = document.getElementById(carId);
            element?.scrollIntoView();
            if (isInView(element)) {
                sessionStorage.setItem(
                    'f6e73b048ec0b88fbb5e5a5da9d5f2a4a4e4f27ce4d8a1a96877f18983f02e2',
                    'false'
                );
                sessionStorage.setItem('carId', '');
                setAllowScroll(false);
            }
        }
    };
    const setCarIdInSessionStorageForScrolling = (id: string) => {
        sessionStorage.setItem('carId', id);
        sessionStorage.setItem(
            'f6e73b048ec0b88fbb5e5a5da9d5f2a4a4e4f27ce4d8a1a96877f18983f02e2',
            'true'
        );
    };
    // _______________________________________________________

    function calculateListingView(
        displayedListings: CarListingData[] = [],
        exchangeCarBannerIndex: number
    ) {
        const calculateFinanceCardIndex = () => {
            if (!displayedListings.length) return 0;
            if (displayedListings.length < 6 && displayedListings.length > 3) {
                return Math.ceil(displayedListings.length / 2) + 1;
            } else return displayedListings.length - 1;
        };

        const calculateReserveSeeCarIndex = () => {
            if (!displayedListings.length) return 0;
            if (displayedListings.length < 6 && displayedListings.length > 3) {
                return Math.ceil(displayedListings.length / 2) + 1;
            } else return displayedListings.length - 1;
        };

        let reserveSeeCarIndex =
            displayedListings.length >= 6 ? 4 : calculateReserveSeeCarIndex();
        if (displayedListings.length < reserveSeeCarIndex) {
            reserveSeeCarIndex = 0;
        }

        let valuePropositionIndex = width > 1100 ? 7 : 6;
        if (displayedListings.length < valuePropositionIndex) {
            valuePropositionIndex = reserveSeeCarIndex + 1;
        }

        let financeCarIndex =
            displayedListings.length >= 9
                ? Math.min(
                      valuePropositionIndex + 3,
                      displayedListings.length - 1
                  )
                : calculateFinanceCardIndex();

        if (displayedListings.length < financeCarIndex) {
            financeCarIndex = valuePropositionIndex + 1;
        }

        const results = [];
        let carIndexUsed = 0;
        const sortedCars = sortCars(displayedListings, sortingOptions);
        let bannerShifting = 0;
        const bannerRendered = {
            exchangeCarBanner: false,
            valueProposition: false,
            reserveSeeCar: false,
            financeCar: false,
        };

        const totalBlocks = sortedCars.length + 4;

        const isAppliedFilters = filters.length > 0;
        const isAppliedSorting = !!sortingOptions?.field;

        for (let i = 0; i < totalBlocks; i++) {
            if (
                !bannerRendered.reserveSeeCar &&
                i === reserveSeeCarIndex + 1 + bannerShifting
            ) {
                bannerRendered.reserveSeeCar = true;
                bannerShifting++;
                results.push(
                    <ConditionalClientComponent
                        condition={bannerRendered.reserveSeeCar}
                        key={`banner-reserveSeeCar-${i}`}
                    >
                        <div
                            style={{
                                flex: 1,
                            }}
                        >
                            <ReserveSeeCarBanner
                                variant={desktopView ? 'card' : 'banner'}
                            />
                        </div>
                    </ConditionalClientComponent>
                );
                continue;
            }

            if (
                !bannerRendered.valueProposition &&
                i === valuePropositionIndex + 1 + bannerShifting
            ) {
                bannerRendered.valueProposition = true;
                bannerShifting++;
                results.push(
                    <ConditionalClientComponent
                        condition={bannerRendered.valueProposition}
                        key={`banner-valueProposition-${i}`}
                    >
                        <div className={styles.value_proposition}>
                            <ValuePropositionSlider
                                bannerHeight={1732}
                                bannerWidth={1280}
                            />
                        </div>
                    </ConditionalClientComponent>
                );
                continue;
            }

            if (
                !bannerRendered.exchangeCarBanner &&
                i === exchangeCarBannerIndex + 1 + bannerShifting
            ) {
                bannerRendered.exchangeCarBanner = true;
                bannerShifting++;
                results.push(
                    <ConditionalClientComponent
                        key={`banner-exchangeCarBanner-${i}`}
                        condition={bannerRendered.exchangeCarBanner}
                    >
                        <div
                            style={{
                                gridColumn: ' 1 / -1',
                            }}
                        >
                            {exchangeCarBanner}
                        </div>
                    </ConditionalClientComponent>
                );
                continue;
            }

            if (
                !bannerRendered.financeCar &&
                i === financeCarIndex + 1 + bannerShifting
            ) {
                bannerRendered.financeCar = true;
                bannerShifting++;
                results.push(
                    <ConditionalClientComponent
                        condition={bannerRendered.financeCar}
                        key={`banner-financeCar-${i}`}
                    >
                        {financeCarCard}
                    </ConditionalClientComponent>
                );
                continue;
            }

            const item = sortedCars[carIndexUsed];
            if (!item) {
                continue;
            }

            results.push(
                <div
                    className={'cardofcar'}
                    id={item.id}
                    key={`car-${item.id}`}
                    onClick={() =>
                        setCarIdInSessionStorageForScrolling(item?.id)
                    }
                >
                    <CarDisplayCardClient
                        id={item.id}
                        cardTitle={`${t(item.make)} ${item.model} ${item.year}`}
                        carName={item.sfVehicleName}
                        trim={item.trim}
                        installmentPrice={item?.installmentPrice}
                        totalPrice={item.sylndrSellingPrice}
                        kilometers={item.kilometers}
                        year={item.year}
                        transmission={item.transmission}
                        isPurchaseInProgress={item.isPurchaseInProgress}
                        images={item?.images}
                        isReserved={item.isReserved}
                        make={item.make}
                        model={item.model}
                        sourceScreen={'Cars Listing'}
                        appliedFilter={isAppliedFilters}
                        appliedSort={isAppliedSorting}
                        position={carIndexUsed}
                        discountPrice={item?.discountPrice}
                        isNewlyAdded={item.isNewlyAdded}
                    />
                </div>
            );
            carIndexUsed++;
        }
        // add can't find my car at the end.
        results.push(
            <div
                key={'banner-cant-find-my-car'}
                style={{
                    flex: 1,
                }}
            >
                {cantFindCarCard}
            </div>
        );

        return results;
    }

    const sortedCars = useMemo(() => {
        return calculateListingView(filteredListings, exchangeCarBannerIndex);
    }, [
        filteredListings,
        sortingOptions,
        exchangeCarBannerIndex,
        countToRender,
    ]);

    return (
        <>
            <div className={styles.filters_container}>
                {desktopView && (
                    <Filters
                        comboList={comboList}
                        filters={filters}
                        setFilters={setFilters}
                        displayedCount={resultsCount}
                        bodyTypes={bodyTypes}
                        lang={lang}
                    />
                )}
            </div>
            <div className={styles.banners_container_mobile}>
                {!desktopView && <RetailBanners bannersData={bannersData} />}
            </div>
            <div className={styles.filters_container_mobile}>
                {!desktopView && (
                    <FilterHeaderMobile
                        comboList={comboList}
                        filters={filters}
                        setFilters={setFilters}
                        displayedCount={resultsCount}
                        bodyTypes={bodyTypes}
                        lang={lang}
                    />
                )}
            </div>
            <div className={styles.buy_used_cars_container}>
                <div className={styles.banners_container}>
                    {desktopView && <RetailBanners bannersData={bannersData} />}
                </div>
                <div className={styles.sort_desktop_container}>
                    {desktopView && <Sort filteredCount={resultsCount} />}
                </div>
                {!desktopView && (
                    <Typography color={colors.gray} variant={'body1Bold'}>
                        {`${resultsCount} ${t('Sylndr certified cars sort')}`}
                    </Typography>
                )}
                <div className={styles.cars_listing_container}>
                    {sortedCars}
                </div>
            </div>
        </>
    );
};

export default CarsListing;
